/* eslint-disable complexity, sonarjs/cognitive-complexity, react/jsx-boolean-value, import/no-cycle */
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Element } from 'react-scroll';
import { BCOR_STEP_ONE_EL } from '../../../../brands/default/constants';
import { CALENDAR_ON_REDEMPTION_CONTAINER } from '../../../../brands/default/content/components/deal';
import { convertBookingProductToBasketProduct } from '../../../../helpers/booking';
import { isLightPage } from '../../../../helpers/browser';
import {
  getIsPaypalFeatured,
  isBookingCalendarOnRedemptionGiftDeal,
} from '../../../../helpers/deals';
import {
  setFilteredProducts,
  setQuantity,
  setSelection,
} from '../../../../redux/actions/bcor';
import PayPalMessaging from '../../../PayPalMessaging';
import Message, { MESSAGE_TYPES } from '../../../_generic/Message';
import Spinner from '../../../_generic/spinner/Spinner';
import BasketSocialCues from '../../../checkout/BasketSocialCues';
import KlarnaMessaging from '../../../klarna/KlarnaMessaging';
import BookingCalendarOnRedemption from './BookingCalendarOnRedemption';
import BookingCalendarStep from './BookingCalendarStep';
import CalendarOnRedemptionNights from './CalendarOnRedemptionNights';
import CalendarOnRedemptionProducts from './CalendarOnRedemptionProducts';
import CalendarSocialCue from './CalendarSocialCue';
import {
  CalendarBuyButtons,
  getBuyButtonDeal,
  TravelCalendarBuyButtons,
} from './calendar';

const CalendarOnRedemptionContainer = ({
  deal,
  isCalendarOnly = false,
  isCalendarHidden = false,
  isGift,
  isTravel = false,
  setIsOptionSelected = () => {},
  buttonRef = undefined,
}) => {
  const [
    hasError,
    filteredProducts,
    nightOptions,
    products,
    quantity,
    selectedNights,
    selection,
  ] = useSelector((state) => [
    state.bcor.hasError,
    state.bcor.filteredProducts,
    state.bcor.nightOptions,
    state.bcor.products,
    state.bcor.quantity,
    state.bcor.selectedNights,
    state.bcor.selection,
  ]);
  const dispatch = useDispatch();
  const { bcorGiftMessage, bcorStepOne, bcorStepOneSingleNight, bcorStepTwo } =
    CALENDAR_ON_REDEMPTION_CONTAINER;
  const isPaypalFeatured = getIsPaypalFeatured(deal);
  const isBookingCalendarOnRedemptionGift =
    isBookingCalendarOnRedemptionGiftDeal(deal);

  const selectProduct = (productId) => {
    if (productId) {
      const selectedProduct = products.find(
        (product) => product.tiers[0].dealVoucherProductId === productId,
      );
      dispatch(setSelection(selectedProduct.tiers[0]));
      setIsOptionSelected(true);
    }
  };

  const buyButtonDeal = getBuyButtonDeal({
    convertBookingProductToBasketProduct,
    deal,
    deposit: null,
    flights: null,
    quantity,
    selection,
  });

  const selectQuantity = (newQuantity) => {
    dispatch(setQuantity(Number.parseInt(newQuantity, 10)));
  };

  const onSelectProduct = (id) => {
    if (id !== selection?.dealVoucherProductId) {
      selectProduct(id);
    }
  };

  const isButtonClickValid = () => {
    return buyButtonDeal;
  };

  useEffect(() => {
    if (selectedNights) {
      const nightsInt = Number.parseInt(selectedNights, 10);
      const newFilteredProducts = products.filter((product) => {
        return product.tiers[0].numberOfNights === nightsInt;
      });
      if (newFilteredProducts && newFilteredProducts.length > 0) {
        dispatch(setFilteredProducts(newFilteredProducts));

        // select first product if current selection not in newFilteredProducts
        if (selection) {
          let isInFiltered = false;
          newFilteredProducts.forEach((product) => {
            if (
              product.tiers[0].dealVoucherProductId ===
              selection.dealVoucherProductId
            ) {
              isInFiltered = true;
            }
          });
          if (!isInFiltered) {
            dispatch(setSelection(newFilteredProducts[0].tiers[0]));
          }
        }
      }
    }
  }, [selectedNights]);

  if (isLightPage()) {
    return null;
  }

  return (
    <>
      {!isCalendarOnly && (
        <>
          <Element name={BCOR_STEP_ONE_EL} />
          {nightOptions && Array.isArray(nightOptions) && (
            <>
              <BookingCalendarStep
                stepIndex="1"
                title={
                  nightOptions.length === 1
                    ? bcorStepOneSingleNight
                    : bcorStepOne
                }
              />
              <CalendarOnRedemptionNights />
            </>
          )}

          {!filteredProducts && !hasError && (
            <div className="spinner-container">
              <Spinner />
            </div>
          )}
          {hasError && (
            <Message
              extraClasses="message-no-background message-center message-border-radius"
              message="Sorry there was a problem getting the products."
              showIcon={false}
              type={MESSAGE_TYPES.error}
            />
          )}
        </>
      )}

      {filteredProducts && (
        <>
          {!isCalendarOnly && (
            <>
              <BookingCalendarStep stepIndex="2" title={bcorStepTwo} />
              <CalendarOnRedemptionProducts
                currency={deal?.currency}
                onQuantityChange={selectQuantity}
                onSelect={onSelectProduct}
                products={filteredProducts}
                quantity={quantity}
                selection={selection}
              />

              <CalendarSocialCue deal={deal} />
              {isBookingCalendarOnRedemptionGift && isGift && (
                <>
                  <Message
                    iconPosition="left"
                    message={bcorGiftMessage}
                    showIcon
                  />
                </>
              )}

              {isTravel
                ? TravelCalendarBuyButtons({
                    buttonRef,
                    buyButtonDeal,
                    deposit: null,
                    isBookingCalendarOnRedemption: true,
                    isButtonClickValid,
                    isGiftable: deal.giftable,
                  })
                : CalendarBuyButtons({
                    buyButtonDeal,
                    isBookingCalendarOnRedemption: true,
                    isButtonClickValid,
                    isGift,
                  })}

              <div className="klarna-container">
                <KlarnaMessaging deal={deal} sideBar />
              </div>

              <PayPalMessaging
                deal={deal}
                isFeatured={isPaypalFeatured}
                isFeaturedSlot
              />

              <div className="cues-container">
                <BasketSocialCues />
              </div>
            </>
          )}
          {!isCalendarHidden && (
            <BookingCalendarOnRedemption
              deal={deal}
              isBookingCalendarOnRedemption={true}
              isReadyForCalendar={true}
              quantity={quantity}
              selection={selection}
            />
          )}
        </>
      )}

      <style jsx>{`
        .spinner-container {
          height: 300px;
          position: relative;
        }
        .klarna-container {
          margin-bottom: 10px;
        }
        .cues-container {
          margin-bottom: 20px;
        }
      `}</style>
    </>
  );
};

CalendarOnRedemptionContainer.propTypes = {
  buttonRef: PropTypes.any,
  deal: PropTypes.object.isRequired,
  isCalendarHidden: PropTypes.bool,
  isCalendarOnly: PropTypes.bool,
  isGift: PropTypes.bool,
  isTravel: PropTypes.bool,
  setIsOptionSelected: PropTypes.func,
};

export default CalendarOnRedemptionContainer;
